import { withSubmitGuard } from "../submitGuard.js";
import { esc } from "../escape.js";
import { formatMoney, currencySymbol } from "../currency.js";

// Top-level Promos panel (extracted from the Receipt Adjustments panel
// 2026-07-06): stat tiles + a card grid of the business's promo codes, plus
// the create/edit form modal. Codes themselves still live in
// state.discountCodes / the discount_codes table - only the UI moved.
class PromoView {
  _panel     = document.querySelector("#promoPanel");
  _stats     = document.querySelector("#promoStats");
  _grid      = document.querySelector("#promoGrid");
  _formModal = document.querySelector("#discountCodeModal");

  // ── Open / Close ─────────────────────────────────────────────────────────────

  open() {
    this._panel.classList.remove("hidden", "cashflow-exiting");
  }

  close() {
    this._panel.classList.add("cashflow-exiting");
    setTimeout(() => {
      this._panel.classList.add("hidden");
      this._panel.classList.remove("cashflow-exiting");
    }, 220);
  }

  // ── Rendering ────────────────────────────────────────────────────────────────

  // A code is "used up" when its limited redemptions are exhausted - still
  // status 'active' in the DB, but no longer redeemable at checkout.
  _codeState(dc) {
    if (dc.status === "paused") return "paused";
    if (dc.usageLimit !== null && dc.usageCount >= dc.usageLimit) return "usedup";
    return "active";
  }

  render(codes) {
    this._renderStats(codes);
    this._grid.innerHTML = codes.length
      ? codes.map((dc) => this._cardMarkup(dc)).join("")
      : this._emptyMarkup();
  }

  _renderStats(codes) {
    this._stats.classList.toggle("hidden", codes.length === 0);
    if (!codes.length) return;
    const active = codes.filter((c) => this._codeState(c) === "active").length;
    const paused = codes.filter((c) => this._codeState(c) !== "active").length;
    const redeems = codes.reduce((s, c) => s + (c.usageCount ?? 0), 0);

    const tile = (mod, icon, value, label) => `
      <div class="promo-stat promo-stat--${mod}">
        <span class="promo-stat-icon" aria-hidden="true">${icon}</span>
        <span class="promo-stat-text">
          <span class="promo-stat-value">${value}</span>
          <span class="promo-stat-label">${label}</span>
        </span>
      </div>`;

    this._stats.innerHTML = `
      ${tile("active", `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="19" height="19"><path d="M2 9a3 3 0 0 1 0 6v3a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3a3 3 0 0 1 0-6V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/></svg>`, active, `Active code${active === 1 ? "" : "s"}`)}
      ${tile("paused", `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="19" height="19"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>`, paused, "Paused or used up")}
      ${tile("redeems", `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="19" height="19"><polyline points="20 6 9 17 4 12"/></svg>`, redeems, `Redemption${redeems === 1 ? "" : "s"}`)}`;
  }

  _cardMarkup(dc) {
    const state = this._codeState(dc);
    const pillText = { active: "Active", paused: "Paused", usedup: "Used up" }[state];
    const valueText = dc.type === "percentage" ? `${dc.value}% off` : `${formatMoney(dc.value)} off`;

    let usageHtml;
    if (dc.usageLimit === null) {
      usageHtml = `<span class="promo-usage-text">${dc.usageCount} redeemed &middot; no limit</span>`;
    } else {
      const pct = Math.min(100, (dc.usageCount / dc.usageLimit) * 100);
      usageHtml = `
        <div class="promo-usage-bar"><span class="promo-usage-fill" style="width:${pct}%"></span></div>
        <span class="promo-usage-text">${dc.usageCount} of ${dc.usageLimit} redeemed</span>`;
    }

    return `
      <article class="promo-card promo-card--${state}" data-id="${dc.id}">
        <div class="promo-card-top">
          <span class="promo-code-chip">${esc(dc.code)}</span>
          <span class="promo-pill promo-pill--${state}"><span class="promo-pill-dot" aria-hidden="true"></span>${pillText}</span>
        </div>
        <div class="promo-card-mid">
          <span class="promo-value">${valueText}</span>
          <span class="promo-title">${esc(dc.title)}</span>
          ${dc.description ? `<p class="promo-desc">${esc(dc.description)}</p>` : ""}
        </div>
        <div class="promo-usage">${usageHtml}</div>
        <div class="promo-card-foot">
          <button class="promo-act promo-act-toggle" data-id="${dc.id}" type="button">${state === "paused" ? "Activate" : "Pause"}</button>
          <button class="promo-act promo-act-edit" data-id="${dc.id}" type="button">Edit</button>
          <button class="promo-act promo-act--delete promo-act-delete" data-id="${dc.id}" type="button" aria-label="Delete code">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
          </button>
        </div>
      </article>`;
  }

  _emptyMarkup() {
    return `
      <div class="promo-empty">
        <span class="pos-empty-emoji" aria-hidden="true">🎟️</span>
        <p class="pos-empty-title">No promo codes yet</p>
        <p class="pos-empty-sub">Create discount codes like SUMMER20 that<br/>customers can apply at checkout.</p>
        <button class="btn primary pos-empty-add promo-empty-add" type="button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
          New Code
        </button>
      </div>`;
  }

  // ── Create / edit form modal ─────────────────────────────────────────────────

  showForm(code = null) {
    const isEdit = code !== null;

    let usageType = "unlimited";
    let usageCustom = "";
    if (code) {
      if (code.usageLimit === null) usageType = "unlimited";
      else if (code.usageLimit === 1) usageType = "one-time";
      else {
        usageType = "custom";
        usageCustom = code.usageLimit;
      }
    }

    const formEl = this._formModal.querySelector("#discountCodeForm");
    formEl.innerHTML = `
      <button class="modal-close-btn" id="dcFormCloseBtn" type="button">&times;</button>
      <h2 class="edit-form-title">${isEdit ? "Edit Code" : "New Promo Code"}</h2>

      <div class="edit-field">
        <label for="dcTitle">Title</label>
        <input type="text" id="dcTitle" placeholder="e.g. Summer Sale" value="${isEdit ? esc(code.title) : ""}" />
      </div>

      <div class="edit-field">
        <label for="dcCode">Code</label>
        <input type="text" id="dcCode" placeholder="e.g. SUMMER20"
          value="${isEdit ? esc(code.code) : ""}" autocomplete="off" spellcheck="false" />
      </div>

      <div class="edit-field">
        <label for="dcDesc">
          Description
          <span class="field-optional">(optional)</span>
        </label>
        <input type="text" id="dcDesc" placeholder="e.g. Valid for dine-in orders"
          value="${isEdit ? esc(code.description) : ""}" />
      </div>

      <p class="adj-form-sublabel">Discount Type</p>
      <div class="adj-selector" id="dcTypeSelector">
        <button type="button" class="adj-selector-btn ${!isEdit || code.type === "percentage" ? "active" : ""}"
          data-value="percentage">Percentage (%)</button>
        <button type="button" class="adj-selector-btn ${isEdit && code.type === "fixed" ? "active" : ""}"
          data-value="fixed">Fixed (${currencySymbol()})</button>
      </div>
      <input type="hidden" id="dcType" value="${isEdit ? code.type : "percentage"}" />

      <div class="edit-field">
        <label for="dcValue" id="dcValueLabel">
          ${isEdit && code.type === "fixed" ? `Amount (${currencySymbol()})` : "Amount (%)"}
        </label>
        <input type="number" id="dcValue" min="0"
          step="${isEdit && code.type === "fixed" ? "0.01" : "1"}"
          placeholder="0" value="${isEdit ? code.value : ""}" />
      </div>

      <p class="adj-form-sublabel">Usage Limit</p>
      <div class="adj-selector" id="dcUsageSelector">
        <button type="button" class="adj-selector-btn ${usageType === "unlimited" ? "active" : ""}"
          data-value="unlimited">Unlimited</button>
        <button type="button" class="adj-selector-btn ${usageType === "one-time" ? "active" : ""}"
          data-value="one-time">One-time</button>
        <button type="button" class="adj-selector-btn ${usageType === "custom" ? "active" : ""}"
          data-value="custom">Custom</button>
      </div>
      <input type="hidden" id="dcUsageType" value="${usageType}" />

      <div class="edit-field" id="dcCustomUsageField" style="${usageType === "custom" ? "" : "display:none;"}">
        <label for="dcUsageLimit">Max uses</label>
        <input type="number" id="dcUsageLimit" min="2" step="1" placeholder="e.g. 100"
          value="${usageCustom}" />
      </div>

      <div class="adj-form-actions">
        <button type="button" class="btn" id="dcCancelBtn">Cancel</button>
        <button type="button" class="btn primary" id="dcSaveBtn"
          data-edit-id="${isEdit ? code.id : ""}">
          ${isEdit ? "Update" : "Create"}
        </button>
      </div>
    `;

    this._wireForm();
    this._formModal.classList.remove("hidden");
    document.getElementById("dcTitle").focus();
  }

  _wireForm() {
    // Type selector
    const typeSelector = document.getElementById("dcTypeSelector");
    typeSelector?.addEventListener("click", (e) => {
      const btn = e.target.closest(".adj-selector-btn");
      if (!btn) return;
      typeSelector
        .querySelectorAll(".adj-selector-btn")
        .forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      document.getElementById("dcType").value = btn.dataset.value;
      const label = document.getElementById("dcValueLabel");
      const input = document.getElementById("dcValue");
      if (btn.dataset.value === "fixed") {
        label.textContent = `Amount (${currencySymbol()})`;
        input.step = "0.01";
      } else {
        label.textContent = "Amount (%)";
        input.step = "1";
      }
    });

    // Usage selector
    const usageSelector = document.getElementById("dcUsageSelector");
    usageSelector?.addEventListener("click", (e) => {
      const btn = e.target.closest(".adj-selector-btn");
      if (!btn) return;
      usageSelector
        .querySelectorAll(".adj-selector-btn")
        .forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      document.getElementById("dcUsageType").value = btn.dataset.value;
      document.getElementById("dcCustomUsageField").style.display =
        btn.dataset.value === "custom" ? "" : "none";
    });

    // Auto-uppercase, no spaces
    document.getElementById("dcCode")?.addEventListener("input", (e) => {
      const pos = e.target.selectionStart;
      e.target.value = e.target.value.toUpperCase().replace(/\s/g, "");
      e.target.setSelectionRange(pos, pos);
    });

    document
      .getElementById("dcFormCloseBtn")
      ?.addEventListener("click", () => this.closeForm());
    document
      .getElementById("dcCancelBtn")
      ?.addEventListener("click", () => this.closeForm());
  }

  closeForm() {
    this._formModal.classList.add("hidden");
    this._formModal.querySelector("#discountCodeForm").innerHTML = "";
  }

  _getFormData() {
    const usageType = document.getElementById("dcUsageType")?.value;
    let usageLimit = null;
    if (usageType === "one-time") usageLimit = 1;
    else if (usageType === "custom")
      usageLimit = parseInt(document.getElementById("dcUsageLimit")?.value) || null;

    return {
      id: document.getElementById("dcSaveBtn")?.dataset.editId || null,
      title: document.getElementById("dcTitle")?.value.trim(),
      code: document.getElementById("dcCode")?.value.toUpperCase().trim(),
      description: document.getElementById("dcDesc")?.value.trim(),
      type: document.getElementById("dcType")?.value,
      value: parseFloat(document.getElementById("dcValue")?.value),
      usageLimit,
    };
  }

  // ── Handlers ─────────────────────────────────────────────────────────────────

  _addHandlerOpen(handler) {
    document.addEventListener("click", (e) => {
      if (!e.target.closest("[data-action='promos']")) return;
      handler();
    });
  }

  _addHandlerClose(handler) {
    this._panel.querySelector(".promo-back").addEventListener("click", () => handler());
    // Close the form on backdrop click
    this._formModal.addEventListener("click", (e) => {
      if (e.target === this._formModal) this.closeForm();
    });
  }

  _addHandlerNewCode(handler) {
    this._panel.querySelector("#promoNewBtn").addEventListener("click", () => handler());
    // Delegated: the empty-state New Code button (re-rendered on each render)
    this._grid.addEventListener("click", (e) => {
      if (e.target.closest(".promo-empty-add")) handler();
    });
  }

  _addHandlerSave(handler) {
    this._formModal.addEventListener("click", (e) => {
      const btn = e.target.closest("#dcSaveBtn");
      if (!btn) return;
      const data = this._getFormData();
      if (!data.title) { alert("Please enter a title."); return; }
      if (!data.code) { alert("Please enter a code."); return; }
      if (isNaN(data.value) || data.value < 0) { alert("Please enter a valid amount."); return; }
      if (document.getElementById("dcUsageType")?.value === "custom" && !data.usageLimit) {
        alert("Please enter the max number of uses."); return;
      }
      withSubmitGuard(btn, () => handler(data), { busyText: "Saving…" });
    });
  }

  _addHandlerEdit(handler) {
    this._grid.addEventListener("click", (e) => {
      const btn = e.target.closest(".promo-act-edit");
      if (!btn) return;
      handler(btn.dataset.id);
    });
  }

  _addHandlerDelete(handler) {
    this._grid.addEventListener("click", (e) => {
      const btn = e.target.closest(".promo-act-delete");
      if (!btn) return;
      handler(btn.dataset.id);
    });
  }

  _addHandlerToggleStatus(handler) {
    this._grid.addEventListener("click", (e) => {
      const btn = e.target.closest(".promo-act-toggle");
      if (!btn) return;
      handler(btn.dataset.id);
    });
  }
}

export default new PromoView();
